import React, { useRef, useState } from "react";
import { Download, Upload, Loader2, CheckCircle2, AlertTriangle, ArrowRightLeft } from "lucide-react";
import { Button } from "../ui/button";
import { adminApi } from "../../lib/api";
import { useToast } from "../../hooks/use-toast";

// Preview and production have separate databases. The spreadsheet can't carry
// the launch switches or the reviewer profile, and it refuses to publish unless
// told to — right for an edit, wrong for a move. This is the move.
export const AskCassieTransfer = ({ onRestored }) => {
  const { toast } = useToast();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [withSettings, setWithSettings] = useState(true);
  const [removeExtras, setRemoveExtras] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const download = async () => {
    try {
      const data = await adminApi.askCassie.backup();
      const blob = new Blob([JSON.stringify(data, null, 1)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `ask-cassie-library-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast({ title: "Downloaded", description: `${data.counts.answers} answers · ${data.counts.published} approved · settings included.` });
    } catch {
      toast({ title: "Couldn't download the library", variant: "destructive" });
    }
  };

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPreview(null);
    setFileName(f.name);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        setFile(JSON.parse(String(reader.result || "")));
      } catch {
        setFile(null);
        toast({ title: "That isn't a transfer file", description: "Choose the .json file downloaded from the other site.", variant: "destructive" });
      }
    };
    reader.readAsText(f, "utf-8");
  };

  const run = async (dryRun) => {
    setBusy(true);
    try {
      const res = await adminApi.askCassie.restore({
        backup: file,
        dry_run: dryRun,
        settings: withSettings,
        remove_extras: removeExtras,
      });
      setPreview(res);
      if (res.applied) {
        toast({
          title: "Library restored",
          description: `${res.summary.new} added, ${res.summary.updated} updated, ${res.summary.published} approved.`,
        });
        onRestored?.();
      }
    } catch (e) {
      toast({ title: "Couldn't restore that file", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const s = preview?.summary;

  return (
    <div className="rounded-2xl bg-slate-50 p-6" data-testid="ask-cassie-transfer">
      <h4 className="font-display font-bold text-navy text-sm inline-flex items-center gap-2">
        <ArrowRightLeft size={15} className="text-orange" /> One file, everything in it
      </h4>
      <p className="text-xs text-slate-500 mt-1.5 leading-relaxed max-w-3xl">
        Download the whole library — every answer, whether each one is approved, the reviewer profile and the launch
        switches — then upload it on the other site. Nothing is written until you have seen what it would do. Take a
        copy whenever you like: it is also your backup.
      </p>

      <div className="flex flex-wrap gap-3 mt-4">
        <Button onClick={download} className="bg-navy hover:bg-navy/90 text-white rounded-full" data-testid="ask-cassie-backup">
          <Download size={15} className="mr-1.5" /> Download the whole library
        </Button>
        <input ref={fileRef} type="file" accept=".json,application/json" onChange={pick} className="hidden" data-testid="ask-cassie-transfer-file" />
        <Button onClick={() => fileRef.current?.click()} variant="outline" className="rounded-full" data-testid="ask-cassie-transfer-choose">
          <Upload size={15} className="mr-1.5" /> Choose a library file
        </Button>
        <span className="text-sm text-slate-500 self-center">{fileName || "No file chosen"}</span>
      </div>

      {file && (
        <>
          <div className="mt-5 space-y-2.5">
            <label className="flex items-start gap-2.5 text-sm text-slate-700 cursor-pointer">
              <input type="checkbox" checked={withSettings} onChange={(e) => { setWithSettings(e.target.checked); setPreview(null); }} className="mt-0.5 w-4 h-4 accent-orange" data-testid="transfer-with-settings" />
              <span>
                Bring the settings too
                <span className="block text-xs text-slate-500">Whether the library is launched, whether the old FAQ page is retired, and the reviewer profile.</span>
              </span>
            </label>
            <label className="flex items-start gap-2.5 text-sm text-slate-700 cursor-pointer">
              <input type="checkbox" checked={removeExtras} onChange={(e) => { setRemoveExtras(e.target.checked); setPreview(null); }} className="mt-0.5 w-4 h-4 accent-orange" data-testid="transfer-remove-extras" />
              <span>
                Delete answers that aren't in the file
                <span className="block text-xs text-slate-500">Off by default. Leave it off unless you want this site to match the file exactly.</span>
              </span>
            </label>
          </div>

          <div className="flex flex-wrap gap-3 mt-5">
            <Button onClick={() => run(true)} disabled={busy} className="bg-orange hover:bg-orange-dark text-white rounded-full" data-testid="transfer-check">
              {busy ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : null} Check the file
            </Button>
            {preview && !preview.applied && (
              <Button onClick={() => run(false)} disabled={busy} className="bg-teal hover:bg-teal/90 text-white rounded-full" data-testid="transfer-apply">
                Restore {s.new + s.updated} answer{s.new + s.updated === 1 ? "" : "s"}
              </Button>
            )}
          </div>
        </>
      )}

      {preview && (
        <div className="mt-5 rounded-xl p-4 flex items-start gap-2.5 bg-white border border-slate-200" data-testid="transfer-result">
          {preview.applied ? <CheckCircle2 size={16} className="text-teal shrink-0 mt-0.5" /> : <AlertTriangle size={16} className="text-slate-400 shrink-0 mt-0.5" />}
          <div>
            <p className="text-sm text-navy font-semibold">
              {preview.applied ? "Restored" : "Nothing has been written yet — this is what would happen"}
            </p>
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">
              {s.new} added · {s.updated} updated · {s.unchanged} already identical · {s.published} approved
              {s.extras > 0 && ` · ${s.extras} answer${s.extras === 1 ? "" : "s"} here ${s.extras === 1 ? "is" : "are"} not in the file${removeExtras ? " and will be deleted" : " and will be left alone"}`}
              {s.settings ? " · settings included" : " · settings left as they are"}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AskCassieTransfer;
